import type { PlatformAdapter } from '@rudi/api/platform';
import { getApiBaseUrl as getBaseUrl, getSocketUrl as getBaseSocketUrl } from './url-resolver';
import { clearAllAuthState, rememberLoginReturnPath } from './authUtils';

const TOKEN_STORAGE_KEY = 'rudi_auth_tokens';

/**
 * Web implementation of PlatformAdapter.
 *
 * Uses localStorage for token storage, url-resolver for URL derivation,
 * and handles session expiry by clearing auth state and redirecting to /login.
 */
function readStoredAccessToken(): string | null {
  try {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed.accessToken ?? null;
  } catch {
    return null;
  }
}

export const webPlatformAdapter: PlatformAdapter = {
  async getAccessToken(): Promise<string | null> {
    return readStoredAccessToken();
  },

  peekAccessToken: readStoredAccessToken,

  async setAccessToken(token: string | null): Promise<void> {
    try {
      const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (token === null) {
        // If clearing, remove the whole entry
        if (stored) {
          const parsed = JSON.parse(stored);
          parsed.accessToken = null;
          localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(parsed));
        }
        return;
      }
      if (stored) {
        const parsed = JSON.parse(stored);
        parsed.accessToken = token;
        localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(parsed));
      } else {
        // If no existing entry, create a minimal one
        localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({ accessToken: token }));
      }
    } catch {
      // Silently fail on storage errors
    }
  },

  async getRefreshToken(): Promise<string | null> {
    try {
      const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      return parsed.refreshToken ?? null;
    } catch {
      return null;
    }
  },

  async setRefreshToken(token: string | null): Promise<void> {
    try {
      const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (token === null) {
        if (stored) {
          const parsed = JSON.parse(stored);
          parsed.refreshToken = null;
          localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(parsed));
        }
        return;
      }
      if (stored) {
        const parsed = JSON.parse(stored);
        parsed.refreshToken = token;
        localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(parsed));
      } else {
        localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({ refreshToken: token }));
      }
    } catch {
      // Silently fail
    }
  },

  getApiBaseUrl(): string {
    const baseUrl = getBaseUrl();
    return baseUrl ? `${baseUrl}/api` : '/api';
  },

  getSocketUrl(): string {
    return getBaseSocketUrl();
  },

  async onSessionExpired(): Promise<void> {
    if (typeof window === 'undefined') return;

    // Prevent redirect loops
    if (window.location.pathname === '/login') {
      await clearAllAuthState();
      return;
    }

    const alreadyRedirecting = sessionStorage.getItem('session-expired-redirect');
    if (alreadyRedirecting) return;

    sessionStorage.setItem('session-expired-redirect', 'true');
    sessionStorage.setItem('redirecting-to-login', 'true');
    rememberLoginReturnPath();

    console.log('[WebPlatformAdapter] Session expired, redirecting to /login');

    clearAllAuthState().catch(error => {
      console.error('Failed to clear auth state:', error);
    });

    window.location.href = '/login';
  },

  isDevelopment(): boolean {
    try {
      // Vite injects import.meta.env.DEV at build time
      // At runtime in the web adapter, we check for the global
      return import.meta.env?.DEV ?? false;
    } catch {
      return false;
    }
  },
};
